import { mapProductItemsToPriceItems } from './utils/mapProductItemsToPriceItems'
import BottomSheetComponent from '@/features/common/components/BottomSheet'
import { ProductItem } from '@/features/common/components/Overview/parts/ProductItem/types'
import { PriceItem } from '@/features/common/components/Overview/types'
import { Layout } from '@/features/layout/types'
import { useOrderFinalizationTransitionStep } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/useOrderFinalizationTransitionStep'
import OrderFinalizationTransitionActions from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/OrderFinalizationTransitionActions'
import PaymentActions from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/PaymentActions'
import {
    PriceToPayCollapsible,
    PriceToPayStatic,
} from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/PriceToPay'
import { containerStylesheet } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/styles'
import { PaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/types'
import { FC, memo } from 'react'
import { useStyles } from 'react-native-unistyles'

const MemoBottomSheet = memo(BottomSheetComponent)

interface BottomSheetProps {
    layout: Layout
    cartEmpty: boolean
    cartItems: ProductItem[]
    price?: string
    priceItems?: PriceItem[]
    paymentTypes: PaymentType[]
    actionDisabled: boolean
    actionLabel: string
    onCancel: () => void
    onContinue: () => void
    onInsertNote: () => void
    onAction: (paymentType: PaymentType) => Promise<void>
}

const BottomSheet: FC<BottomSheetProps> = ({
    layout,
    cartEmpty,
    cartItems,
    price,
    priceItems = [],
    paymentTypes,
    actionDisabled,
    actionLabel,
    onCancel,
    onContinue,
    onInsertNote,
    onAction,
}) => {
    const [isOrderFinalizationOpened, toggleOrderFinalizationOpened] =
        useOrderFinalizationTransitionStep(layout)

    const { styles } = useStyles(containerStylesheet, { layout, cartEmpty })

    const renderContent = () => {
        if (cartEmpty) return null

        const isTransitionStep = layout === 'compact' && !isOrderFinalizationOpened

        if (isTransitionStep) {
            const handleContinue = () => {
                toggleOrderFinalizationOpened()

                onContinue()
            }

            const staticPriceItems = mapProductItemsToPriceItems(cartItems)

            return (
                <>
                    {price && <PriceToPayStatic price={price} items={staticPriceItems} />}
                    <OrderFinalizationTransitionActions
                        onCancel={onCancel}
                        onContinue={handleContinue}
                    />
                </>
            )
        }

        return (
            <>
                {price && (
                    <PriceToPayCollapsible
                        price={price}
                        items={priceItems}
                        headingVariant={layout === 'compact' ? 'h4' : 'h3'}
                    />
                )}
                <PaymentActions
                    layout={layout}
                    paymentTypes={paymentTypes}
                    actionDisabled={actionDisabled || !paymentTypes.length || cartEmpty}
                    actionLabel={actionLabel}
                    onAction={onAction}
                    onCancel={onCancel}
                    onInsertNote={onInsertNote}
                />
            </>
        )
    }

    return <MemoBottomSheet style={styles.container}>{renderContent()}</MemoBottomSheet>
}

export default memo(BottomSheet)
